import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "–");

// Admin: the weekly note email (Tuesdays 08:00 Madrid via platform cron), with a test send and a send-now.
export default function SojournsNotesMail({ headers, adminEmail }) {
  const [state, setState] = useState(null);
  const [testTo, setTestTo] = useState(adminEmail || "");
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/admin/sojourns/notes`, { headers }).then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Add an email to send the test to");
    setBusy("test");
    try {
      await axios.post(`${API}/admin/sojourns/notes/send`, { test_to: testTo.trim() }, { headers });
      toast.success(`Test sent to ${testTo.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the test");
    } finally {
      setBusy("");
    }
  };

  const sendNow = async () => {
    if (!state?.next) return;
    if (!window.confirm(`Send note ${state.next.n} to ${state.recipients} people now?`)) return;
    setBusy("send");
    try {
      const r = await axios.post(`${API}/admin/sojourns/notes/send`, {}, { headers });
      toast.success(`Sending note ${r.data.slug ? state.next.n : ""} to ${r.data.recipients} people`);
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the send");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-6" data-testid="admin-sojourns-notes-mail">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-medium text-[#2E2A26]">Weekly note email</h2>
          <p className="text-xs text-[#3D4035]/60 mt-1 max-w-xl">
            Every Tuesday at 08:00 (Madrid) the next note goes to everyone on the interest list, in order, and stops by itself once the gate opens. Each email has an unsubscribe link.
          </p>
        </div>
        <Button onClick={sendNow} disabled={!state?.next || state?.gate_open || busy === "send"} variant="outline" className="rounded-full" data-testid="admin-notes-send-now">
          <Send className="w-4 h-4 mr-2" /> {busy === "send" ? "Sending" : "Send this week's note now"}
        </Button>
      </div>

      {state && (
        <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
          <div className="rounded-lg bg-[#FBF7F1] p-3" data-testid="admin-notes-next">
            <p className="text-[10px] uppercase tracking-wider text-[#3D4035]/50 mb-1">Next note</p>
            <p className="text-[#2E2A26]">{state.gate_open ? "Gate is open, series finished." : state.next ? `${state.next.n} · ${state.next.title}` : "All six notes sent."}</p>
          </div>
          <div className="rounded-lg bg-[#FBF7F1] p-3" data-testid="admin-notes-recipients">
            <p className="text-[10px] uppercase tracking-wider text-[#3D4035]/50 mb-1">On the list</p>
            <p className="text-[#2E2A26]">{state.recipients} people (unsubscribed excluded)</p>
          </div>
          <div className="rounded-lg bg-[#FBF7F1] p-3" data-testid="admin-notes-gate">
            <p className="text-[10px] uppercase tracking-wider text-[#3D4035]/50 mb-1">Gate opens</p>
            <p className="text-[#2E2A26]">{state.reveal_date || "not set"}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mt-4">
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="notes-test-to" className="block text-xs text-[#3D4035]/60 mb-1">Send me a test of the next note</label>
          <input id="notes-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="h-10 w-full px-3 rounded-lg border border-stone-200 text-sm bg-white" data-testid="admin-notes-test-email" />
        </div>
        <Button onClick={sendTest} disabled={busy === "test" || !state?.next} className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full h-10" data-testid="admin-notes-send-test">
          <Mail className="w-4 h-4 mr-2" /> {busy === "test" ? "Sending" : "Send test"}
        </Button>
      </div>

      {state?.sends?.length > 0 && (
        <table className="w-full text-xs mt-5" data-testid="admin-notes-log">
          <thead className="text-left text-[#3D4035]/50 uppercase tracking-wider">
            <tr><th className="py-1.5 pr-3">Sent</th><th className="py-1.5 pr-3">Note</th><th className="py-1.5 pr-3">Delivered</th><th className="py-1.5">Failed</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {state.sends.map((s) => (
              <tr key={s.slug} data-testid={`admin-notes-log-${s.slug}`}>
                <td className="py-1.5 pr-3 whitespace-nowrap text-[#3D4035]/70">{fmt(s.sent_at)}</td>
                <td className="py-1.5 pr-3 text-[#2E2A26]">{s.n} · {s.title}</td>
                <td className="py-1.5 pr-3">{s.sent}</td>
                <td className="py-1.5">{s.failed || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
